import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { Box, Button, styled, Typography } from "@material-ui/core";
import type { FC } from "react";
import { EXAM_ICONS, EXAM_LABELS } from "./epocus-exam-constants";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";
import { useEnabledExamTypes } from "./use-enabled-exam-types";

const ExamGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
});

const ExamTypeBtn = styled(Button)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  padding: "12px 8px",
  borderRadius: 10,
  border: `1.5px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  color: EPOCUS_TEXT_PRIMARY,
  fontWeight: 600,
  fontSize: rem(13),
  textTransform: "none",
  minHeight: 72,
  "&:hover": {
    borderColor: EPOCUS_PRIMARY,
    backgroundColor: EPOCUS_SELECTED_BG,
  },
  "&[aria-pressed='true']": {
    borderColor: EPOCUS_PRIMARY,
    backgroundColor: EPOCUS_SELECTED_BG,
    color: EPOCUS_PRIMARY,
  },
});

/**
 * Reusable exam type picker grid.
 *
 * @param allowedTypes  When provided, narrows further — e.g. to the exam types
 *                      the captured DICOM preset makes plausible.
 *                      null/undefined = every exam type the program enables.
 * @param selected      Currently selected exam type (for aria-pressed highlight).
 * @param onChange      Called when the user taps an exam type button.
 */
export const ExamTypePicker: FC<{
  allowedTypes?: ExamType[] | null;
  selected: ExamType | null;
  onChange: (type: ExamType) => void;
}> = ({ allowedTypes, selected, onChange }) => {
  const enabledTypes = useEnabledExamTypes();
  const narrowed = allowedTypes
    ? enabledTypes.filter((type) => allowedTypes.includes(type))
    : enabledTypes;
  const types = narrowed.length > 0 ? narrowed : enabledTypes;
  return (
    <ExamGrid>
      {types.map((type) => {
        const ExamIcon = EXAM_ICONS[type];
        return (
          <ExamTypeBtn
            key={type}
            onClick={() => onChange(type)}
            aria-pressed={selected === type}
            disableRipple={false}
          >
            {ExamIcon && (
              <ExamIcon
                width={24}
                height={24}
                fill={selected === type ? EPOCUS_PRIMARY : EPOCUS_ICON_ACTIVE}
                aria-hidden
              />
            )}
            {EXAM_LABELS[type]}
          </ExamTypeBtn>
        );
      })}
    </ExamGrid>
  );
};

/**
 * Descriptive heading for the picker when shown above the grid.
 * Explains why the options are filtered (if they are).
 */
export const ExamTypePickerHint: FC<{
  dominantPreset: string | null;
  feasibleTypes: ExamType[] | null;
}> = ({ dominantPreset, feasibleTypes }) => {
  if (!dominantPreset || !feasibleTypes) {
    return (
      <Typography
        variant="body2"
        style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(13) }}
      >
        Select the exam type for this scan.
      </Typography>
    );
  }
  if (feasibleTypes.length === 1) {
    return (
      <Typography
        variant="body2"
        style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(13) }}
      >
        Scan captured in{" "}
        <strong style={{ color: EPOCUS_TEXT_PRIMARY }}>{dominantPreset}</strong>{" "}
        preset — confirm the exam type below.
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(13) }}
    >
      Scan captured in{" "}
      <strong style={{ color: EPOCUS_TEXT_PRIMARY }}>{dominantPreset}</strong>{" "}
      preset — select which exam type applies.
    </Typography>
  );
};
